/**
 * Ask the record — the Hub's panel for `POST /projects/:code/ask` (ADR-0045).
 *
 * The Engine answers FROM THE RECORD: every sentence names its sources, a figure the record does not
 * carry never prints, and a refusal is an honest answer rather than a failure. All of that is decided
 * on the server. This panel's job is narrower and easy to get wrong: say exactly what came back, and
 * never let one outcome read as another.
 *
 * ## ⚠️ What it must never do
 *
 * - **Blur a failure into a gap.** "Nothing on this record answers that", "the Engine failed", "this
 *   account may not ask" and "the question never arrived" are four different facts. Rendering any of
 *   them as another tells a person the record lacks an answer it may well hold.
 * - **Hide what was left out.** When the search did not answer, or the record was too large for one
 *   question, the reply says so — and so does this panel, on EVERY outcome, a refusal included.
 * - **Ask on mount.** Tabs are hidden, not unmounted (`ReportSections`), so a mount-time call would
 *   spend a model call for every visit to the project page. Nothing is sent until a person asks.
 *
 * ## Why the words are ours
 *
 * Chad's Stage 3.5 carries governed UI sentences ("Chat GGP", "The record is answering"). Whether to
 * speak them is ADR-0047 §8 question 2 — Brad's, and open — so these are plain words of our own.
 */

import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { askProject, getSession, type AskReply, type AskSentence } from '../api.js';
import { documentDownloadUrl, retrieveDocument } from '../retrieval.js';
import { signInPathFor } from '../return-path.js';

/** The longest question the route accepts (`ASK_QUESTION_MAX_CHARS`). */
const QUESTION_MAX = 1_000;

/**
 * Where to start — ADR-0035 §7.4's staff questions, verbatim in intent: "what's open · why the stop ·
 * who owns it". They are the questions the record is best placed to answer, so a first use succeeds.
 */
export const STARTER_QUESTIONS = [
  'What is open on this record?',
  'Why is there a stop?',
  'Who owns the open items?',
] as const;

/** The FETCHING half. The rendering half below is pure, so every state can be asserted. */
export function ProjectAsk({ code }: { code: string }) {
  const navigate = useNavigate();
  const [question, setQuestion] = useState('');
  const [asked, setAsked] = useState<string | null>(null);
  const [reply, setReply] = useState<AskReply | null>(null);
  const [busy, setBusy] = useState(false);

  const ask = async (raw: string) => {
    const q = raw.trim();
    // A second submit while the first is in flight would spend a second model call on the same words.
    if (!q || busy) return;
    setBusy(true);
    setAsked(q);
    setReply(null);
    try {
      const r = await askProject(code, q);
      if (r.kind === 'signed-out') {
        // The session is gone past refresh. Route to sign-in, returning here — the same exit the
        // drawings and documents panels take, so a lapsed session never looks like an empty answer.
        void navigate(signInPathFor(window.location, window.location.origin), { replace: true });
        return;
      }
      setReply(r);
    } catch {
      // `askProject` resolves by contract; this is the belt to its braces. A rejection must still end
      // in an OUTCOME — a question with nothing under it reads as "no answer", which is a claim.
      setReply({ kind: 'error', message: 'The answer could not be read.' });
    } finally {
      setBusy(false);
    }
  };

  /** Open a cited document — the documents panel's flow, verbatim; see `ProjectDrawings`. */
  const onRetrieve = (documentId: string) => {
    void (async () => {
      const outcome = await retrieveDocument(
        {
          openTab: () => window.open('', '_blank'),
          ensureSession: async () => (await getSession()) !== null,
          goTo: (url) => window.location.assign(url),
        },
        code,
        documentId,
      );
      if (outcome === 'signed-out')
        void navigate(signInPathFor(window.location, window.location.origin), { replace: true });
    })();
  };

  return (
    <AskPanel
      code={code}
      question={question}
      onQuestionChange={setQuestion}
      onAsk={(q) => {
        setQuestion(q);
        void ask(q);
      }}
      busy={busy}
      asked={asked}
      reply={reply}
      onRetrieve={onRetrieve}
    />
  );
}

/** The panel proper — pure. `reply` is null before anything has been asked, and while asking. */
export function AskPanel({
  code,
  question,
  onQuestionChange,
  onAsk,
  busy,
  asked,
  reply,
  onRetrieve,
}: {
  code: string;
  question: string;
  onQuestionChange: (q: string) => void;
  onAsk: (q: string) => void;
  busy: boolean;
  asked: string | null;
  reply: AskReply | null;
  onRetrieve?: (documentId: string) => void;
}) {
  const submit = (e: FormEvent) => {
    e.preventDefault();
    onAsk(question);
  };

  return (
    <section className="card">
      <h2>Ask the record</h2>
      <p className="muted">
        Answers come only from this project&rsquo;s record and its documents, and every sentence
        names its source. Nothing on the record changes from here.
      </p>

      <form onSubmit={submit}>
        <label className="df__field df__field--full">
          <span className="df__label">Your question</span>
          <textarea
            value={question}
            maxLength={QUESTION_MAX}
            rows={3}
            disabled={busy}
            onChange={(e) => onQuestionChange(e.target.value)}
          />
          <span className="df__hint">
            It reports what the record holds. It will not compute a new figure, advise, or predict.
          </span>
        </label>
        <button type="submit" disabled={busy || question.trim() === ''}>
          {busy ? 'Asking…' : 'Ask'}
        </button>
      </form>

      {asked === null && (
        <p className="muted">
          Try:{' '}
          {STARTER_QUESTIONS.map((q, i) => (
            <span key={q}>
              {i > 0 && ' · '}
              <button type="button" className="link-btn" disabled={busy} onClick={() => onAsk(q)}>
                {q}
              </button>
            </span>
          ))}
        </p>
      )}

      <div aria-live="polite">
        {asked !== null && (
          <p>
            <strong>Asked:</strong> {asked}
          </p>
        )}
        {busy && <p className="muted">Asking the record…</p>}
        {!busy && reply && <AskReplyView code={code} reply={reply} onRetrieve={onRetrieve} />}
      </div>
    </section>
  );
}

function AskReplyView({
  code,
  reply,
  onRetrieve,
}: {
  code: string;
  reply: AskReply;
  onRetrieve?: (documentId: string) => void;
}) {
  switch (reply.kind) {
    case 'answered':
      return (
        <>
          <ul className="ask__answers">
            {reply.answers.map((a, i) => (
              <li key={i}>
                {a.sentence} <Sources code={code} sentence={a} onRetrieve={onRetrieve} />
              </li>
            ))}
          </ul>
          {reply.withheld > 0 && (
            <p className="muted">
              {reply.withheld} further sentence{reply.withheld === 1 ? ' was' : 's were'} withheld —
              {reply.withheld === 1 ? ' it' : ' they'} could not be traced to the record.
            </p>
          )}
          <LeftOut leftOut={reply.leftOut} />
        </>
      );
    case 'refused':
      return (
        <>
          <div className="notice">
            {reply.refusal.text}
            {reply.refusal.what && <> Missing: {reply.refusal.what}.</>}
          </div>
          <LeftOut leftOut={reply.leftOut} />
        </>
      );
    case 'engine_failed':
      // ⚠️ Deliberately NOT the "nothing on this record" notice: the Engine failed; the record did not.
      return (
        <>
          <div className="notice warn">{reply.text}</div>
          <LeftOut leftOut={reply.leftOut} />
        </>
      );
    case 'forbidden':
      return (
        <p className="muted">
          Asking the record is <strong>staff-only</strong>, and this account may not. That is a
          permission, not a fault.
        </p>
      );
    case 'rate_limited':
      return (
        <div className="notice warn">
          Too many questions in a minute. Wait a moment, then ask again.
        </div>
      );
    case 'error':
      return (
        <div className="notice error">
          The question could not be answered: {reply.message} This does <strong>not</strong> mean
          the record has no answer.
        </div>
      );
    case 'signed-out':
      // The container navigates to sign-in before this could render; kept total for the type.
      return null;
  }
}

/**
 * The sources of one sentence. A document passage is a LINK to the attributable retrieval route —
 * built by `documentDownloadUrl`, never by hand, because the Hub and the api are different ORIGINS. A
 * record source (the project record, the underwrite, a variance, a fact) has no file behind it and is
 * named, not linked.
 */
function Sources({
  code,
  sentence,
  onRetrieve,
}: {
  code: string;
  sentence: AskSentence;
  onRetrieve?: (documentId: string) => void;
}) {
  if (sentence.sources.length === 0) return null;
  return (
    <span className="muted">
      (
      {sentence.sources.map((s, i) => (
        <span key={s.ref}>
          {i > 0 && '; '}
          {s.documentId ? (
            <a
              href={documentDownloadUrl(code, s.documentId)}
              onClick={(e) => {
                if (!onRetrieve || !s.documentId) return;
                e.preventDefault();
                onRetrieve(s.documentId);
              }}
            >
              {s.label}
            </a>
          ) : (
            s.label
          )}
        </span>
      ))}
      )
    </span>
  );
}

/** What the answer could not use — said on every outcome, because a gap in what was READ is not a gap in the record. */
function LeftOut({ leftOut }: { leftOut: string[] }) {
  if (leftOut.length === 0) return null;
  return <p className="muted">This answer could not use: {leftOut.join(' · ')}.</p>;
}
